import { Link, Navigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useArrivalInformation } from '../hooks/useArrivalInformation';
import type { ArrivalInformation } from '../lib/portal';

/**
 * Local Travel Options page — Route: /travel
 *
 * Shows transport and getting-around information drawn from the same
 * arrival-information payload as ArrivalInformationPage (shared queryKey →
 * no extra request when navigating from the dashboard card).
 *
 * Pre-placement or if the endpoint is unavailable, redirects to /dashboard
 * declaratively (never via navigate() during render), matching the pattern in
 * ArrivalInformationPage and the card's own silent-absence contract.
 */

/** ISO weekday number (1 = Monday … 7 = Sunday) to short label. */
const weekday = (d: number) =>
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d - 1] ?? String(d);

export default function LocalTravelPage() {
  const { data, isLoading, isError } = useQuery(useArrivalInformation());

  if (isLoading) {
    return <div className="card skeleton">Loading travel information…</div>;
  }

  if (isError || !data) {
    return (
      <div className="card" role="alert">
        <p style={{ margin: 0 }}>Unable to load travel information. Please try again later.</p>
      </div>
    );
  }

  // Pre-placement: declarative redirect (never call navigate() during render).
  if (!data.available) return <Navigate to="/dashboard" replace />;

  const info: ArrivalInformation = data;

  const hasAirportInfo = info.portOfEntryAirport || info.nearestAirportToJobsite;
  const hasArrivalWindow =
    info.preferredArrivalDays.length > 0 || info.preferredArrivalTimes;

  return (
    <>
      <header className="pagehead">
        <div>
          <h1>
            <MapPin size={20} aria-hidden /> Local Travel Options
          </h1>
          {info.hostName && (
            <p style={{ margin: 0, color: 'var(--ink-2)' }}>
              Getting around your destination for {info.hostName}.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link to="/arrival-information" className="btn-outline">Arrival information</Link>
          <Link to="/dashboard" className="btn-outline">Back to dashboard</Link>
        </div>
      </header>

      {hasAirportInfo && (
        <div className="card" data-testid="travel-airports">
          <h2 style={{ marginTop: 0 }}>Airports</h2>
          <ul className="feed">
            {info.portOfEntryAirport && (
              <li><strong>Port of entry airport:</strong> {info.portOfEntryAirport}</li>
            )}
            {info.nearestAirportToJobsite && (
              <li><strong>Nearest airport to jobsite:</strong> {info.nearestAirportToJobsite}</li>
            )}
          </ul>
        </div>
      )}

      <div className="card" data-testid="travel-transport">
        <h2 style={{ marginTop: 0 }}>Pick-up &amp; transport</h2>
        {info.pickUpProvided ? (
          <ul className="feed">
            <li><strong>Pick-up provided:</strong> Yes</li>
            {/* transportPickUpCost is only non-null when pickUpProvided is true
                (contract rule); absent cost with pick-up means it's included. */}
            <li><strong>Pick-up cost:</strong> {info.transportPickUpCost ?? 'Included'}</li>
          </ul>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            No transport pick-up is provided for this placement. You will need to arrange
            your own onward travel from the airport.
          </p>
        )}
      </div>

      {hasArrivalWindow && (
        <div className="card" data-testid="travel-arrival-window">
          <h2 style={{ marginTop: 0 }}>Preferred arrival window</h2>
          <ul className="feed">
            {info.preferredArrivalDays.length > 0 && (
              <li>
                <strong>Preferred days:</strong>{' '}
                {info.preferredArrivalDays.map(weekday).join(', ')}
              </li>
            )}
            {info.preferredArrivalTimes && (
              <li><strong>Preferred times:</strong> {info.preferredArrivalTimes}</li>
            )}
          </ul>
        </div>
      )}

      {info.generalArrivalInstructions && (
        <div className="card" data-testid="travel-instructions">
          <h2 style={{ marginTop: 0 }}>Additional travel notes</h2>
          <p className="globus-msg" style={{ margin: 0 }}>
            {info.generalArrivalInstructions}
          </p>
        </div>
      )}
    </>
  );
}
